import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * @typedef {Object} GwpRefs
 * @property {HTMLElement} [banner]
 */

/** @extends {Component<GwpRefs>} */
class GwpOfferComponent extends Component {
  #abortController = new AbortController();

  /** @type {boolean} */
  #adding = false;

  /** @type {boolean} */
  #wasGiftInCart = false;

  /** @type {boolean} */
  #wasBannerVisible = false;

  /** @type {string|null} */
  #bannerTextSnapshot = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#wasGiftInCart = this.#giftInCart;
    this.#wasBannerVisible = this.#isBannerVisible;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.#maybeAutoAdd();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Auto-add gift if threshold met and not manually dismissed,
   * or show re-add banner if manually dismissed.
   */
  #maybeAutoAdd() {
    if (this.#adding || this.#giftInCart || !this.#thresholdMet) return;

    if (this.#manuallyDismissed) {
      this.#showBanner();
      return;
    }

    this.#addGift();
  }

  /**
   * Reveal the hidden re-add banner with animation.
   */
  #showBanner() {
    const banner = this.refs.banner;
    if (!banner) return;
    banner.classList.remove('hidden');
    banner.style.animation = 'gwp-banner-in 300ms ease-out';
  }

  /**
   * Animate the banner out (fade + zoom), resolves when done.
   * @returns {Promise<void>}
   */
  #hideBanner() {
    const banner = this.refs.banner;
    if (!banner || banner.classList.contains('hidden')) return Promise.resolve();

    banner.classList.add('gwp-banner--hiding');
    return new Promise((resolve) => {
      banner.addEventListener('animationend', () => resolve(), { once: true });
    });
  }

  /**
   * Animate banner exit using the morphed element.
   * Restores the pre-morph text content so the old message is visible during fade-out.
   * @returns {Promise<void>}
   */
  #animateBannerExit() {
    const banner = this.refs.banner;
    if (!banner) return Promise.resolve();

    // Restore pre-morph text so "add gift" text doesn't flash during exit
    if (this.#bannerTextSnapshot) {
      const textEl = banner.querySelector('.gwp-banner__text');
      if (textEl) textEl.innerHTML = this.#bannerTextSnapshot;
    }

    banner.classList.remove('hidden');
    banner.classList.add('gwp-banner--hiding');
    return new Promise((resolve) => {
      banner.addEventListener('animationend', () => {
        banner.classList.remove('gwp-banner--hiding');
        banner.classList.add('hidden');
        resolve();
      }, { once: true });
    });
  }

  /**
   * Add gift to cart via Cart API, with banner exit animation.
   */
  async #addGift() {
    if (this.#adding) return;
    this.#adding = true;

    await this.#hideBanner();
    const body = JSON.stringify({
      items: [
        {
          id: Number(this.dataset.variantId),
          quantity: 1,
          properties: { _is_gift: this.dataset.offerHandle },
        },
      ],
      sections: this.#sectionId,
      sections_url: window.location.pathname,
    });

    try {
      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok) return;

      this.#dispatchUpdate(data, { animate: true });
    } catch {
      // Silently fail — gift add is not critical
    } finally {
      this.#adding = false;
    }
  }

  /**
   * Remove gift from cart via Cart API with removal animation.
   * Finds the gift line item in the DOM by data-is-gift attribute.
   */
  async #removeGift() {
    const container = this.closest('dialog, .shopify-section');
    if (!container) return;

    const giftItem = container.querySelector('.cart-item[data-is-gift]');
    if (!(giftItem instanceof HTMLElement)) return;

    const removeButton = giftItem.querySelector('[data-cart-line]');
    if (!(removeButton instanceof HTMLElement)) return;

    const line = Number(removeButton.dataset.cartLine);
    if (!line) return;

    const body = JSON.stringify({
      line,
      quantity: 0,
      sections: this.#sectionId,
      sections_url: window.location.pathname,
    });

    try {
      const [, res] = await Promise.all([
        this.#animateRemoval(giftItem),
        fetch(Theme.routes.cart_change_url, fetchConfig('json', { body })),
      ]);
      const data = await res.json();

      if (!res.ok) return;

      this.#dispatchUpdate(data);
    } catch {
      // Silently fail
    }
  }

  /**
   * Collapse animation for a cart item being removed.
   * @param {HTMLElement} item
   * @returns {Promise<void>}
   */
  #animateRemoval(item) {
    item.style.maxHeight = `${item.offsetHeight}px`;
    // eslint-disable-next-line no-unused-expressions
    item.offsetHeight;
    item.classList.add('cart-item--removing');

    return new Promise((resolve) => {
      item.addEventListener('transitionend', (e) => {
        if (e.target === item && e.propertyName === 'max-height') resolve();
      });
    });
  }

  /**
   * Re-add gift (declarative event handler: on:click="/addGift").
   */
  addGift = () => {
    sessionStorage.removeItem(this.#storageKey);
    this.#addGift();
  };

  /**
   * Handle cart update events from other components.
   * Sets a pre-morph snapshot so updatedCallback can detect manual removal.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartUpdateEvent)) return;
    if (event.detail?.data?.source === 'gwp-offer') return;

    // Snapshot state before morph replaces DOM and attributes
    this.#wasGiftInCart = this.#giftInCart;
    this.#wasBannerVisible = this.#isBannerVisible;

    // Save text content for exit animation (avoids showing "add gift" text during fade-out)
    const banner = this.refs.banner;
    if (this.#wasBannerVisible && banner) {
      this.#bannerTextSnapshot = banner.querySelector('.gwp-banner__text')?.innerHTML ?? null;
    } else {
      this.#bannerTextSnapshot = null;
    }
  };

  /**
   * Called after section morph re-renders this component.
   * Detects manual removal and threshold drops.
   */
  updatedCallback() {
    super.updatedCallback?.();

    // Gift was in cart before morph but is gone now
    if (this.#wasGiftInCart && !this.#giftInCart) {
      if (this.#thresholdMet) {
        // Threshold still met → user manually removed the gift
        sessionStorage.setItem(this.#storageKey, 'true');
        this.#showBanner();
      } else {
        // Threshold dropped → we (or the system) removed it, clear flag
        sessionStorage.removeItem(this.#storageKey);
      }
    }

    // If threshold dropped, clear dismiss flag and auto-remove gift if still present
    if (!this.#thresholdMet) {
      sessionStorage.removeItem(this.#storageKey);
      if (this.#giftInCart) {
        this.#removeGift();
      }
    }

    // Threshold newly met after morph → animate banner exit, then auto-add or show re-add
    if (this.#thresholdMet && !this.#giftInCart && !this.#wasGiftInCart) {
      if (this.#wasBannerVisible) {
        this.#animateBannerExit().then(() => this.#maybeAutoAdd());
      } else {
        this.#maybeAutoAdd();
      }
    }

    // Banner newly appeared after morph → animate entrance
    if (this.#isBannerVisible && !this.#wasBannerVisible) {
      const banner = this.refs.banner;
      if (banner) {
        banner.style.animation = 'gwp-banner-in 300ms ease-out';
      }
    }

    this.#wasGiftInCart = this.#giftInCart;
    this.#wasBannerVisible = this.#isBannerVisible;
  }

  /**
   * @param {unknown} data
   * @param {{animate?: boolean}} [options]
   */
  #dispatchUpdate(data, options) {
    const typed = /** @type {{sections?: Record<string, string>, item_count?: number}} */ (data);
    const html = typed.sections?.[this.#sectionId];

    if (html) {
      morphSection(this.#sectionId, html, 'hydration');
    }

    // /cart/add.js doesn't return item_count — read it from the morphed dialog DOM
    const itemCount = typed.item_count
      ?? Number(document.querySelector('[data-hydration-key="cart-drawer-inner"] [data-cart-count]')?.textContent);

    if (options?.animate) {
      const dialog = document.querySelector(`[data-hydration-key="cart-drawer-inner"]`);
      const giftItem = dialog?.querySelector('.cart-item[data-is-gift]');
      if (giftItem instanceof HTMLElement) {
        // Hide immediately (same frame as morph, before browser paints)
        giftItem.classList.add('cart-item--new');
        // Animate reveal on next frame
        requestAnimationFrame(() => this.#animateReveal(giftItem));
      }
    }

    if (Number.isFinite(itemCount)) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = String(itemCount);
      });
    }

    document.dispatchEvent(
      new CartUpdateEvent(data, 'gwp-offer', {
        source: 'gwp-offer',
        sections: typed.sections,
        itemCount: Number.isFinite(itemCount) ? itemCount : undefined,
      })
    );
  }

  /**
   * Reveal animation for a new cart item.
   * @param {HTMLElement} item
   */
  #animateReveal(item) {
    const naturalHeight = item.offsetHeight;

    item.style.overflow = 'hidden';
    item.style.opacity = '0';
    item.style.translate = '0 -10px';
    item.style.maxHeight = '0px';
    item.style.marginTop = '0px';
    // eslint-disable-next-line no-unused-expressions
    item.offsetHeight;

    item.classList.add('cart-item--reveal');
    item.style.maxHeight = `${naturalHeight}px`;
    item.style.opacity = '1';
    item.style.translate = '0 0';
    item.style.marginTop = '';

    item.addEventListener('transitionend', (e) => {
      if (e.target === item && e.propertyName === 'max-height') {
        item.classList.remove('cart-item--reveal');
        item.style.maxHeight = '';
        item.style.opacity = '';
        item.style.translate = '';
        item.style.overflow = '';
      }
    });
  }

  get #isBannerVisible() {
    const banner = this.refs.banner;
    return !!banner && !banner.classList.contains('hidden');
  }

  get #giftInCart() {
    return this.hasAttribute('data-gift-in-cart');
  }

  get #thresholdMet() {
    return this.hasAttribute('data-threshold-met');
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #storageKey() {
    return `gwp:dismissed:${this.dataset.variantId}`;
  }

  get #manuallyDismissed() {
    return sessionStorage.getItem(this.#storageKey) === 'true';
  }
}

if (!customElements.get('gwp-offer-component')) {
  customElements.define('gwp-offer-component', GwpOfferComponent);
}
